import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PencilSimple, Trash, Check, X, Package } from "@phosphor-icons/react";

export default function EntriesTable({ deliveries, carriers, closed, onUpdate, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const startEdit = (d) => {
    setEditingId(d.id);
    setEdit({ carrier: d.carrier, packages: d.packages, time: d.time, notes: d.notes || "" });
  };

  const save = async () => {
    setBusy(true);
    try {
      await onUpdate(editingId, { ...edit, packages: parseInt(edit.packages, 10) });
      setEditingId(null);
    } finally {
      setBusy(false);
    }
  };

  const total = deliveries.reduce((s, d) => s + d.packages, 0);

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid="entries-table">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          Today's deliveries
        </div>
        <div className="text-sm font-semibold text-slate-700" data-testid="entries-count">
          {deliveries.length} {deliveries.length === 1 ? "entry" : "entries"}
        </div>
      </div>
      {deliveries.length === 0 ? (
        <div className="p-10 text-center" data-testid="entries-empty">
          <Package size={40} weight="duotone" className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">
            {closed
              ? "No deliveries were logged for this day."
              : "No deliveries logged yet — add the first one above."}
          </p>
        </div>
      ) : (
        <table className="w-full text-sm text-left text-slate-600">
          <thead className="text-xs uppercase bg-slate-50 text-slate-500 border-b border-slate-200 font-semibold tracking-wider">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Carrier</th>
              <th className="px-4 py-3">Packages</th>
              <th className="px-4 py-3">Notes</th>
              {!closed && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {deliveries.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`entry-row-${d.id}`}>
                {editingId === d.id ? (
                  <>
                    <td className="px-4 py-2">
                      <Input
                        data-testid="edit-time-input"
                        type="time"
                        value={edit.time}
                        onChange={(e) => setEdit({ ...edit, time: e.target.value })}
                        className="h-8 w-28"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Select value={edit.carrier} onValueChange={(v) => setEdit({ ...edit, carrier: v })}>
                        <SelectTrigger data-testid="edit-carrier-select" className="h-8 w-40 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {carriers.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2">
                      <Input
                        data-testid="edit-packages-input"
                        type="text"
                        inputMode="numeric"
                        value={edit.packages}
                        onChange={(e) => setEdit({ ...edit, packages: e.target.value })}
                        className="h-8 w-24"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Input
                        data-testid="edit-notes-input"
                        type="text"
                        value={edit.notes}
                        onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                        className="h-8"
                      />
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        data-testid="edit-save-btn"
                        onClick={save}
                        disabled={busy}
                        className="text-emerald-600 hover:text-emerald-700 p-1"
                        title="Save"
                      >
                        <Check size={18} weight="bold" />
                      </button>
                      <button
                        data-testid="edit-cancel-btn"
                        onClick={() => setEditingId(null)}
                        className="text-slate-400 hover:text-slate-600 p-1"
                        title="Cancel"
                      >
                        <X size={18} weight="bold" />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-semibold text-slate-900" data-testid={`entry-time-${d.id}`}>{d.time}</td>
                    <td className="px-4 py-3" data-testid={`entry-carrier-${d.id}`}>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                        {d.carrier}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900" data-testid={`entry-packages-${d.id}`}>{d.packages}</td>
                    <td className="px-4 py-3 text-slate-500" data-testid={`entry-notes-${d.id}`}>{d.notes || "—"}</td>
                    {!closed && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`edit-entry-btn-${d.id}`}
                          onClick={() => startEdit(d)}
                          className="text-blue-600 hover:text-blue-700 p-1"
                          title="Edit"
                        >
                          <PencilSimple size={18} weight="bold" />
                        </button>
                        <button
                          data-testid={`delete-entry-btn-${d.id}`}
                          onClick={() => onDelete(d.id)}
                          className="text-red-500 hover:text-red-600 p-1"
                          title="Delete"
                        >
                          <Trash size={18} weight="bold" />
                        </button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-orange-50 border-t border-orange-200">
              <td className="px-4 py-3 font-bold text-slate-900" colSpan={2}>TOTAL</td>
              <td className="px-4 py-3 font-bold text-orange-600" data-testid="entries-total">{total}</td>
              <td colSpan={closed ? 1 : 2}></td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
